import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Abrechnung as Sicht } from "@kundeninfo/api-client";
import { Auswahl, Hinweis, Schalter, Tabelle } from "@kundeninfo/ui/me-design";
import { formatBetrag } from "../format.js";
import "./Abrechnung.css";

/**
 * DER LESEKASTEN „Abrechnung" am Auftragsdetail (Spec, „Soll/Ist je Leistungsjahr").
 *
 * Je Auftragsposition: beauftragt, abgerechnet, im Entwurf, offen — dazu die Gesamtzeile,
 * die AUCH die Rechnungspositionen OHNE Zuordnung mitzählt (`abgerechnetGesamt`).
 *
 * **Die REIHENFOLGE kommt vom Server** und wird hier nicht noch einmal gebildet:
 * überzogene Positionen zuerst (Hausregel „Schlechtestes oben", wörtlich wie in der Spec),
 * dann die offenen, dann die erledigten. Eine zweite Sortierung in der Maske wäre eine
 * zweite Wahrheit — und die erste Abweichung fiele erst auf, wenn jemand die Liste mit dem
 * Auftrag vergleicht.
 *
 * **Der Umschalter „je Bestellposition"** fasst Zeilen mit derselben `customerOrderLine`
 * zusammen. Das ist reine Anzeige: Die Beträge werden addiert, die Reihenfolge bleibt die
 * der ersten Zeile jeder Gruppe.
 *
 * **Eigener CSS-Block `abr__`.**
 */

export type AbrechnungsClient = Pick<ApiClient, "getAbrechnung">;

export type AbrechnungProps = {
  client: AbrechnungsClient;
  customerId: string;
  orderId: string;
  currency?: string;
};

type Zeile = Sicht["zeilen"][number];

function centAus(betrag: string): number {
  return Math.round(Number(betrag) * 100);
}

/** Zeilen mit derselben Bestellposition zu EINER zusammenfassen — Beträge addiert, Mengen
 *  nur, wo beide Seiten eine tragen. */
function jeBestellposition(zeilen: readonly Zeile[]): Zeile[] {
  const gruppen = new Map<string, Zeile>();
  for (const zeile of zeilen) {
    const key = zeile.customerOrderLine ?? `#${zeile.orderItemId}`;
    const bisher = gruppen.get(key);
    if (bisher === undefined) {
      gruppen.set(key, { ...zeile, name: zeile.customerOrderLine ?? zeile.name });
      continue;
    }
    const summe = (a: string, b: string) => ((centAus(a) + centAus(b)) / 100).toFixed(2);
    const menge = (a: number | null, b: number | null) => (
      a === null || b === null ? null : a + b);
    gruppen.set(key, {
      ...bisher,
      beauftragt: summe(bisher.beauftragt, zeile.beauftragt),
      abgerechnet: summe(bisher.abgerechnet, zeile.abgerechnet),
      imEntwurf: summe(bisher.imEntwurf, zeile.imEntwurf),
      offen: summe(bisher.offen, zeile.offen),
      beauftragteMenge: menge(bisher.beauftragteMenge, zeile.beauftragteMenge),
      abgerechneteMenge: menge(bisher.abgerechneteMenge, zeile.abgerechneteMenge),
      offeneMenge: menge(bisher.offeneMenge, zeile.offeneMenge),
    });
  }
  return [...gruppen.values()];
}

export function Abrechnung(props: AbrechnungProps): ReactElement {
  const { client, customerId, orderId, currency = "EUR" } = props;
  const [sicht, setSicht] = useState<Sicht | null>(null);
  const [jahr, setJahr] = useState<string | null>(null);
  const [gruppiert, setGruppiert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const lauf = useRef(0);
  const getAbrechnung = client.getAbrechnung;

  useEffect(() => {
    const meiner = ++lauf.current;
    setSicht(null);
    setFehler(null);
    getAbrechnung(customerId, orderId, jahr ?? undefined)
      .then((antwort) => {
        if (lauf.current !== meiner) return;
        setSicht(antwort);
      })
      .catch(() => {
        if (lauf.current !== meiner) return;
        setFehler("Die Abrechnung konnte nicht geladen werden.");
      });
    return () => { lauf.current++; };
  }, [getAbrechnung, customerId, orderId, jahr]);

  // Ein Wechsel des AUFTRAGS setzt auch die Jahreswahl zurück — sonst zeigte der neue
  // Auftrag ein Leistungsjahr, das es an ihm gar nicht gibt.
  useEffect(() => { setJahr(null); }, [orderId]);

  const zeilen = useMemo(
    () => (sicht === null ? [] : gruppiert ? jeBestellposition(sicht.zeilen) : sicht.zeilen),
    [sicht, gruppiert]);

  if (fehler !== null) return <Hinweis art="fehler">{fehler}</Hinweis>;
  if (sicht === null) return <p className="abr__laedt">Die Abrechnung wird geladen …</p>;

  return (
    <section className="abr" aria-label="Abrechnung">
      <div className="abr__kopf">
        <h3 className="abr__titel">Abrechnung</h3>
        {/* me-auswahl (ME-Etappe 3, Aufgabe 5) statt des rohen `<select>`; die Kennung
            bleibt. Kompakt: Jahreswahl und Umschalter sind eine Werkzeugleiste über der
            Tabelle — die Beschriftung bleibt für die Vorlesestimme. */}
        <Auswahl id="abr-jahr" name="abr-jahr" kompakt beschriftung="Leistungsjahr"
          wert={sicht.periodStart ?? ""} onGeaendert={(d) => setJahr(d.wert)}>
          {sicht.jahre.map((j) => (
            <option key={j.periodStart} value={j.periodStart}>
              {j.periodStart.slice(0, 4)}
            </option>
          ))}
        </Auswahl>
        {/* Ein echtes Ja/Nein — also `Schalter` (me-schalter) statt des Kästchens. */}
        <Schalter id="abr-gruppiert" name="abr-gruppiert" beschriftung="je Bestellposition"
          an={gruppiert} onGeaendert={(d) => setGruppiert(d.an)} />
      </div>

      {sicht.jahre.length === 0 && (
        <Hinweis art="info">
          Ohne Laufzeit gibt es keine Leistungsjahre — und damit nichts abzurechnen.
        </Hinweis>
      )}

      {/* me-tabelle: Rahmen, Zebra und das waagerechte Rollen bringt das Element; Beträge
          rechtsbündig über `data-ausrichtung` an th UND td. */}
      {sicht.jahre.length > 0 && (
        <Tabelle>
          <table className="abr__tabelle">
            <thead>
              <tr>
                <th scope="col">Position</th>
                <th scope="col" data-ausrichtung="rechts">Beauftragt</th>
                <th scope="col" data-ausrichtung="rechts">Abgerechnet</th>
                <th scope="col" data-ausrichtung="rechts">Im Entwurf</th>
                <th scope="col" data-ausrichtung="rechts">Offen</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((zeile) => (
                <tr key={`${zeile.periodStart}|${zeile.orderItemId}|${zeile.sortIndex}`}
                  className={Number(zeile.offen) < 0 ? "abr__zeile--ueberzogen" : undefined}>
                  <td>
                    {zeile.name}
                    {!gruppiert && zeile.customerOrderLine !== null && (
                      <span className="abr__bestellposition">{zeile.customerOrderLine}</span>
                    )}
                  </td>
                  <td className="ki-num" data-ausrichtung="rechts">
                    {formatBetrag(Number(zeile.beauftragt), currency)}
                  </td>
                  <td className="ki-num" data-ausrichtung="rechts">
                    {formatBetrag(Number(zeile.abgerechnet), currency)}
                  </td>
                  <td className="ki-num" data-ausrichtung="rechts">
                    {formatBetrag(Number(zeile.imEntwurf), currency)}
                  </td>
                  <td className="ki-num" data-ausrichtung="rechts">
                    {formatBetrag(Number(zeile.offen), currency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="abr__gesamt">
                <th scope="row">
                  Gesamt
                  {sicht.gesamt.abgerechnetGesamt !== sicht.gesamt.abgerechnet && (
                    <span className="abr__fussnote">
                      einschliesslich pauschal abgerechneter Zeilen
                    </span>
                  )}
                </th>
                <td className="ki-num" data-ausrichtung="rechts">
                  {formatBetrag(Number(sicht.gesamt.beauftragt), currency)}
                </td>
                <td className="ki-num" data-ausrichtung="rechts"
                  data-testid="abr-gesamt-abgerechnet">
                  {formatBetrag(Number(sicht.gesamt.abgerechnetGesamt), currency)}
                </td>
                <td className="ki-num" data-ausrichtung="rechts">
                  {formatBetrag(Number(sicht.gesamt.imEntwurf), currency)}
                </td>
                <td className="ki-num" data-ausrichtung="rechts" data-testid="abr-gesamt-offen">
                  {formatBetrag(Number(sicht.gesamt.offen), currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </Tabelle>
      )}
    </section>
  );
}
